/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { useAtom } from 'jotai'
import {
  Button,
  Flex,
  Heading,
  Item,
  NumberField,
  Picker,
} from '@adobe/react-spectrum'
import { Switch } from '@adobe/react-spectrum'

import { DefaultViewerConfig, viewerAtom } from '../config'
import { FormItem } from '../components/FormItem'
import { A3dModelCamera } from '@a3d-viewer/viewer'
import { useMemo } from 'react'

type CameraPanelProps = {
  handleReset: () => void
  cameras: A3dModelCamera[]
}

export function CameraPanel({ handleReset, cameras }: CameraPanelProps) {
  const [atom, setAtom] = useAtom(viewerAtom)

  const handleCameraOrbitChange = (property: string) => (value: number) => {
    const orbit = atom.camera.orbit
    // @ts-ignore
    orbit[property] = value
    setAtom((prev) => ({ ...prev, camera: { ...prev.camera, orbit } }))
  }

  const handleCameraTargetChange = (axis: string) => (value: number) => {
    const target = atom.camera.target
    // @ts-ignore
    target[axis] = value
    setAtom((prev) => ({ ...prev, camera: { ...prev.camera, target } }))
  }

  const handleResetButtonPress = () => {
    setAtom((prev) => ({
      ...prev,
      camera: {
        ...DefaultViewerConfig.camera,
      },
    }))
    handleReset()
  }

  const setCameraOrbitOn = (value: boolean) => {
    setAtom((prev) => ({
      ...prev,
      camera: {
        ...prev.camera,
        orbitOn: value,
        selectedCamera: undefined,
      },
    }))
  }

  const setCameraTargetOn = (value: boolean) => {
    setAtom((prev) => ({
      ...prev,
      camera: {
        ...prev.camera,
        targetOn: value,
        selectedCamera: undefined,
      },
    }))
  }

  const setCameraName = (value: string | undefined) => {
    setAtom((prev) => ({
      ...prev,
      camera: {
        ...prev.camera,
        selectedCamera: value,
        targetOn: false,
        orbitOn: false,
      },
    }))
  }

  const camerasList = useMemo(() => {
    return (
      <>
        <Item key="none">None</Item>
        {cameras.map((camera) => (
          <Item key={camera.cameraName}>{camera.cameraName}</Item>
        ))}
      </>
    )
  }, cameras)

  return (
    <>
      <Heading level={1}>
        <Flex justifyContent="space-between" alignItems="center">
          <span>Camera</span>
          <Button variant="primary" onPress={handleResetButtonPress}>
            Reset
          </Button>
        </Flex>
      </Heading>

      {cameras.length > 0 && (
        <>
          <Heading level={2}>Mesh Cameras</Heading>
          <FormItem>
            <Picker
              label="Cameras"
              selectedKey={atom.camera.selectedCamera ?? 'none'}
              onSelectionChange={(value) => {
                if (value === 'none') {
                  setCameraName(undefined)
                }
                setCameraName(value as string)
              }}
            >
              {camerasList}
            </Picker>
          </FormItem>
        </>
      )}

      <Heading level={2}>Position</Heading>
      <FormItem>
        <Switch isSelected={atom.camera.orbitOn} onChange={setCameraOrbitOn}>
          Override orbit
        </Switch>
      </FormItem>
      <FormItem>
        <Flex gap="size-100">
          <NumberField
            UNSAFE_className="flex-1 w-auto"
            label="Alpha"
            step={0.1}
            value={atom.camera.orbit.alpha}
            onChange={handleCameraOrbitChange('alpha')}
            isDisabled={!atom.camera.orbitOn}
            minValue={0}
            maxValue={Math.PI}
          />
          <NumberField
            UNSAFE_className="flex-1 w-auto"
            label="Beta"
            step={0.1}
            value={atom.camera.orbit.beta}
            onChange={handleCameraOrbitChange('beta')}
            isDisabled={!atom.camera.orbitOn}
            minValue={0}
            maxValue={Math.PI}
          />
          <NumberField
            UNSAFE_className="flex-1 w-auto"
            label="Radius"
            step={0.1}
            value={atom.camera.orbit.radius}
            onChange={handleCameraOrbitChange('radius')}
            isDisabled={!atom.camera.orbitOn}
            minValue={0}
          />
        </Flex>
      </FormItem>

      <Heading level={2}>Target</Heading>
      <FormItem>
        <Switch isSelected={atom.camera.targetOn} onChange={setCameraTargetOn}>
          Override target
        </Switch>
      </FormItem>
      <FormItem>
        <Flex gap="size-100">
          <NumberField
            UNSAFE_className="flex-1 w-auto"
            label="X"
            step={0.1}
            value={atom.camera.target.x}
            onChange={handleCameraTargetChange('x')}
            isDisabled={!atom.camera.targetOn}
          />

          <NumberField
            UNSAFE_className="flex-1 w-auto"
            label="Y"
            step={0.1}
            value={atom.camera.target.y}
            onChange={handleCameraTargetChange('y')}
            isDisabled={!atom.camera.targetOn}
          />
          <NumberField
            UNSAFE_className="flex-1 w-auto"
            label="Z"
            step={0.1}
            value={atom.camera.target.z}
            onChange={handleCameraTargetChange('z')}
            isDisabled={!atom.camera.targetOn}
          />
        </Flex>
      </FormItem>
    </>
  )
}
